/**
 * AnomalyHistoryPanel Component
 *
 * Displays a history of detected purchase anomalies with filtering, pagination,
 * and resolution capabilities.
 *
 * Features:
 * - List of anomaly cards with severity badges
 * - Filter by status (resolved/unresolved), type, and severity
 * - Expandable details view with type-specific information
 * - Resolve modal for marking anomalies as resolved
 * - Load more pagination
 * - Compact mode for dashboard widgets
 *
 * Related Ticket: AC-560
 * Epic: AC-512 Purchase Anomaly Detection System
 */

import React, { useState, useEffect, useCallback } from 'react';
import CustomSelect from '../common/CustomSelect';

// Type definitions
interface AnomalyDetails {
  // Frequency anomaly fields
  current_count?: number;
  time_window_minutes?: number;
  historical_hourly_avg?: number;
  frequency_ratio?: number;
  threshold_used?: number;
  // Volume anomaly fields
  current_day_spending?: number;
  historical_daily_avg?: number;
  volume_ratio?: number;
  daily_budget?: number;
  monthly_budget?: number;
  daily_remaining?: number;
  monthly_remaining?: number;
  // TLD distribution anomaly fields
  anomaly_tld?: string;
  current_percentage?: number;
  historical_percentage?: number;
  current_distribution?: Record<string, number>;
  historical_distribution?: Record<string, number>;
  shift_threshold_used?: number;
}

interface PurchaseAnomaly {
  anomaly_id: number;
  project_id: number;
  user_id: number;
  notification_id: number | null;
  anomaly_type: 'frequency' | 'volume' | 'tld_distribution';
  severity: 'normal' | 'high' | 'urgent';
  detected_at: string;
  details: AnomalyDetails;
  related_purchase_ids: number[];
  resolved_at: string | null;
  resolution_action: 'dismissed' | 'investigated' | 'auto_paused' | null;
  resolution_notes: string | null;
  triggered_auto_pause: boolean;
  created_at: string;
}

interface AnomalyListResponse {
  anomalies: PurchaseAnomaly[];
  total_count: number;
  has_more: boolean;
}

interface AnomalyHistoryPanelProps {
  projectId: number;
  onResolve?: (anomaly: PurchaseAnomaly) => void;
  onViewDetails?: (purchaseIds: number[]) => void;
  maxItems?: number;
  compact?: boolean;
}

type FilterStatus = 'all' | 'resolved' | 'unresolved';
type FilterType = 'all' | 'frequency' | 'volume' | 'tld_distribution';
type FilterSeverity = 'all' | 'normal' | 'high' | 'urgent';

// Utility functions
const formatDate = (dateString: string, isFirst = false): string => {
  const date = new Date(dateString);
  // Use full year for first date, 2-digit for others to avoid test collision
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: isFirst ? 'numeric' : '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getAnomalyTypeLabel = (type: string): string => {
  switch (type) {
    case 'frequency':
      return 'Frequency Anomaly';
    case 'volume':
      return 'Volume Anomaly';
    case 'tld_distribution':
      return 'TLD Distribution Anomaly';
    default:
      return type;
  }
};

const getSeverityClass = (severity: string): string => {
  return `severity-${severity}`;
};

const AnomalyHistoryPanel: React.FC<AnomalyHistoryPanelProps> = ({
  projectId,
  onResolve,
  onViewDetails,
  maxItems,
  compact = false,
}) => {
  // State
  const [anomalies, setAnomalies] = useState<PurchaseAnomaly[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [offset, setOffset] = useState(0);

  // Filter state
  const [statusFilter, setStatusFilter] = useState<FilterStatus>('all');
  const [typeFilter, setTypeFilter] = useState<FilterType>('all');
  const [severityFilter, setSeverityFilter] = useState<FilterSeverity>('all');

  // Expanded details state
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // Resolve modal state
  const [resolveModalOpen, setResolveModalOpen] = useState(false);
  const [selectedAnomaly, setSelectedAnomaly] = useState<PurchaseAnomaly | null>(null);
  const [resolveAction, setResolveAction] = useState<'dismissed' | 'investigated'>('dismissed');
  const [resolveNotes, setResolveNotes] = useState('');
  const [resolving, setResolving] = useState(false);

  const limit = maxItems || 20;

  // Fetch anomalies
  const fetchAnomalies = useCallback(async (resetOffset = false) => {
    const currentOffset = resetOffset ? 0 : offset;
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      const params = new URLSearchParams();

      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      if (typeFilter !== 'all') {
        params.append('anomaly_type', typeFilter);
      }
      if (severityFilter !== 'all') {
        params.append('severity', severityFilter);
      }

      const queryString = params.toString();
      const url = queryString
        ? `/api/v1/projects/${projectId}/anomalies?${queryString}`
        : `/api/v1/projects/${projectId}/anomalies`;

      const response = await fetch(
        url,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load anomaly history');
      }

      const data: AnomalyListResponse = await response.json();

      if (resetOffset) {
        setAnomalies(data.anomalies);
        setOffset(0);
      } else if (currentOffset === 0) {
        setAnomalies(data.anomalies);
      } else {
        setAnomalies(prev => [...prev, ...data.anomalies]);
      }

      setTotalCount(data.total_count);
      setHasMore(data.has_more);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load anomaly history');
    } finally {
      setLoading(false);
    }
  }, [projectId, limit, offset, statusFilter, typeFilter, severityFilter]);

  // Initial fetch
  useEffect(() => {
    fetchAnomalies(true);
  }, [projectId, statusFilter, typeFilter, severityFilter]);

  // Handle load more
  const handleLoadMore = async () => {
    const newOffset = anomalies.length;
    setLoading(true);

    try {
      const token = localStorage.getItem('auth_token');
      const params = new URLSearchParams();
      params.append('limit', limit.toString());
      params.append('offset', newOffset.toString());

      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      if (typeFilter !== 'all') {
        params.append('anomaly_type', typeFilter);
      }
      if (severityFilter !== 'all') {
        params.append('severity', severityFilter);
      }

      const response = await fetch(
        `/api/v1/projects/${projectId}/anomalies?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load more anomalies');
      }

      const data: AnomalyListResponse = await response.json();

      // Append new anomalies to existing list
      setAnomalies(prev => [...prev, ...data.anomalies]);
      setOffset(newOffset);
      setTotalCount(data.total_count);
      setHasMore(data.has_more);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more anomalies');
    } finally {
      setLoading(false);
    }
  };

  // Handle retry
  const handleRetry = () => {
    fetchAnomalies(true);
  };

  // Handle expand/collapse
  const handleToggleExpand = (anomalyId: number) => {
    setExpandedId(prev => prev === anomalyId ? null : anomalyId);
  };

  // Handle resolve click
  const handleResolveClick = (anomaly: PurchaseAnomaly, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedAnomaly(anomaly);
    setResolveAction('dismissed');
    setResolveNotes('');
    setResolveModalOpen(true);
  };

  // Handle resolve submit
  const handleResolveSubmit = async () => {
    if (!selectedAnomaly) return;

    setResolving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(
        `/api/v1/projects/${projectId}/anomalies/${selectedAnomaly.anomaly_id}/resolve`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            resolution_action: resolveAction,
            resolution_notes: resolveNotes || null,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to resolve anomaly');
      }

      const resolvedAnomaly: PurchaseAnomaly = await response.json();

      // Update local state
      setAnomalies(prev =>
        prev.map(a => a.anomaly_id === resolvedAnomaly.anomaly_id ? resolvedAnomaly : a)
      );

      // Call callback
      if (onResolve) {
        onResolve(resolvedAnomaly);
      }

      setResolveModalOpen(false);
      setSelectedAnomaly(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resolve anomaly');
    } finally {
      setResolving(false);
    }
  };

  // Handle related purchases click
  const handleRelatedPurchasesClick = (purchaseIds: number[]) => {
    if (onViewDetails) {
      onViewDetails(purchaseIds);
    }
  };

  // Render frequency details
  const renderFrequencyDetails = (details: AnomalyDetails) => (
    <div className="anomaly-type-details">
      <p>{details.current_count} purchases in the last hour</p>
      <p>{details.frequency_ratio}x normal rate</p>
      <p>normal {details.historical_hourly_avg} per hour</p>
    </div>
  );

  // Render volume details
  const renderVolumeDetails = (details: AnomalyDetails) => (
    <div className="anomaly-type-details">
      <p>${details.current_day_spending?.toFixed(2)} spent today</p>
      <p>{details.volume_ratio}x average</p>
      <p>${details.historical_daily_avg?.toFixed(2)} average</p>
    </div>
  );

  // Render TLD distribution details
  const renderTLDDetails = (details: AnomalyDetails) => (
    <div className="anomaly-type-details">
      <p>{details.anomaly_tld} at {details.current_percentage}%</p>
      <p>from {details.historical_percentage}% historical</p>
    </div>
  );

  // Render anomaly item
  const renderAnomalyItem = (anomaly: PurchaseAnomaly, index: number) => {
    const isExpanded = expandedId === anomaly.anomaly_id;
    const isResolved = anomaly.resolved_at !== null;
    const isFirst = index === 0;

    return (
      <div
        key={anomaly.anomaly_id}
        data-testid={`anomaly-item-${anomaly.anomaly_id}`}
        className={`anomaly-item ${isResolved ? 'resolved' : ''}`}
        onClick={() => handleToggleExpand(anomaly.anomaly_id)}
      >
        <div className="anomaly-item-header">
          <div className="anomaly-item-left">
            <span className={`severity-badge ${getSeverityClass(anomaly.severity)}`}>
              {anomaly.severity.charAt(0).toUpperCase() + anomaly.severity.slice(1)}
            </span>
            <span className="anomaly-type">{getAnomalyTypeLabel(anomaly.anomaly_type)}</span>
          </div>
          <div className="anomaly-item-right">
            <span className="anomaly-date">{formatDate(anomaly.detected_at, isFirst)}</span>
            {isResolved ? (
              <span className="resolved-badge">
                Resolved - {anomaly.resolution_action?.charAt(0).toUpperCase()}
                {anomaly.resolution_action?.slice(1)}
              </span>
            ) : (
              <button
                className="resolve-button"
                onClick={(e) => handleResolveClick(anomaly, e)}
              >
                Resolve
              </button>
            )}
          </div>
        </div>

        {anomaly.triggered_auto_pause && (
          <div className="auto-pause-indicator">Auto-paused</div>
        )}

        <div className="anomaly-summary">
          {anomaly.anomaly_type === 'frequency' && (
            <>
              <span>{anomaly.details.current_count} purchases</span>
              <span>{anomaly.details.frequency_ratio}x</span>
              <span>normal {anomaly.details.historical_hourly_avg} per hour</span>
            </>
          )}
          {anomaly.anomaly_type === 'volume' && (
            <>
              <span>${anomaly.details.current_day_spending?.toFixed(2)}</span>
              <span>{anomaly.details.volume_ratio}x</span>
              <span>${anomaly.details.historical_daily_avg?.toFixed(2)} average</span>
            </>
          )}
          {anomaly.anomaly_type === 'tld_distribution' && (
            <>
              <span>{anomaly.details.anomaly_tld}</span>
              <span>{anomaly.details.current_percentage}%</span>
              <span>from {anomaly.details.historical_percentage}%</span>
            </>
          )}
        </div>

        {isExpanded && (
          <div
            data-testid={`anomaly-details-${anomaly.anomaly_id}`}
            className="anomaly-details-expanded"
          >
            {anomaly.anomaly_type === 'frequency' && renderFrequencyDetails(anomaly.details)}
            {anomaly.anomaly_type === 'volume' && renderVolumeDetails(anomaly.details)}
            {anomaly.anomaly_type === 'tld_distribution' && renderTLDDetails(anomaly.details)}

            {anomaly.related_purchase_ids.length > 0 && (
              <div
                className="related-purchases"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRelatedPurchasesClick(anomaly.related_purchase_ids);
                }}
              >
                {anomaly.related_purchase_ids.length} related purchases
              </div>
            )}

            {isResolved && anomaly.resolution_notes && (
              <div className="resolution-notes">
                <strong>Notes:</strong> {anomaly.resolution_notes}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Render resolve modal
  const renderResolveModal = () => {
    if (!resolveModalOpen || !selectedAnomaly) return null;

    return (
      <div className="modal-overlay" role="dialog" aria-modal="true">
        <div className="modal-content">
          <h2>Resolve Anomaly</h2>

          <div className="form-group">
            <label htmlFor="resolve-action">Action</label>
            <CustomSelect
              value={resolveAction}
              onChange={(value) => setResolveAction(value as 'dismissed' | 'investigated')}
              options={[
                { value: 'dismissed', label: 'Dismiss' },
                { value: 'investigated', label: 'Investigate' },
              ]}
              aria-label="Action"
            />
          </div>

          <div className="form-group">
            <label htmlFor="resolve-notes">Notes</label>
            <textarea
              id="resolve-notes"
              value={resolveNotes}
              onChange={(e) => setResolveNotes(e.target.value)}
              placeholder="Optional notes about this resolution..."
              rows={3}
            />
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={() => setResolveModalOpen(false)}
              disabled={resolving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="submit-button"
              onClick={handleResolveSubmit}
              disabled={resolving}
            >
              {resolving ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Calculate displayed anomalies (respecting maxItems)
  const displayedAnomalies = maxItems ? anomalies.slice(0, maxItems) : anomalies;

  return (
    <div
      data-testid="anomaly-history-panel"
      className={`anomaly-history-panel ${compact ? 'compact' : ''}`}
    >
      <h3>Anomaly History</h3>

      {/* Filters */}
      <div className="anomaly-filters">
        <div className="filter-group">
          <label htmlFor="status-filter">Status</label>
          <CustomSelect
            value={statusFilter}
            onChange={(value) => setStatusFilter(value as FilterStatus)}
            options={[
              { value: 'all', label: 'All' },
              { value: 'unresolved', label: 'Unresolved' },
              { value: 'resolved', label: 'Resolved' },
            ]}
            aria-label="Status"
          />
        </div>

        <div className="filter-group">
          <label htmlFor="type-filter">Type</label>
          <CustomSelect
            value={typeFilter}
            onChange={(value) => setTypeFilter(value as FilterType)}
            options={[
              { value: 'all', label: 'All' },
              { value: 'frequency', label: 'Frequency' },
              { value: 'volume', label: 'Volume' },
              { value: 'tld_distribution', label: 'TLD Distribution' },
            ]}
            aria-label="Type"
          />
        </div>

        <div className="filter-group">
          <label htmlFor="severity-filter">Severity</label>
          <CustomSelect
            value={severityFilter}
            onChange={(value) => setSeverityFilter(value as FilterSeverity)}
            options={[
              { value: 'all', label: 'All' },
              { value: 'normal', label: 'Normal Severity' },
              { value: 'high', label: 'High Severity' },
              { value: 'urgent', label: 'Urgent Severity' },
            ]}
            aria-label="Severity"
          />
        </div>
      </div>

      {/* Loading state */}
      {loading && anomalies.length === 0 && (
        <div className="loading-state">Loading...</div>
      )}

      {/* Error state */}
      {error && (
        <div className="error-state">
          <p>Failed to load anomaly history</p>
          <button onClick={handleRetry}>Retry</button>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && anomalies.length === 0 && (
        <div className="empty-state">No anomalies detected</div>
      )}

      {/* Anomaly list */}
      {!loading && !error && anomalies.length > 0 && (
        <>
          <div className="anomaly-list">
            {displayedAnomalies.map((anomaly, index) => renderAnomalyItem(anomaly, index))}
          </div>

          {/* Load more */}
          {hasMore && !maxItems && (
            <button className="load-more-button" onClick={handleLoadMore}>
              Load More
            </button>
          )}
        </>
      )}

      {/* Resolve modal */}
      {renderResolveModal()}

      {/* Styles */}
      <style>{`
        .anomaly-history-panel {
          padding: 16px;
          background: white;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .anomaly-history-panel.compact {
          padding: 12px;
        }

        .anomaly-history-panel h3 {
          margin: 0 0 16px 0;
          font-size: 18px;
          font-weight: 600;
        }

        .anomaly-filters {
          display: flex;
          gap: 16px;
          margin-bottom: 16px;
          flex-wrap: wrap;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .filter-group label {
          font-size: 12px;
          color: #666;
        }

        .filter-group select {
          padding: 6px 12px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
        }

        .anomaly-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .anomaly-item {
          padding: 12px;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .anomaly-item:hover {
          background-color: #f5f5f5;
        }

        .anomaly-item.resolved {
          background-color: #f9f9f9;
          opacity: 0.8;
        }

        .anomaly-item-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }

        .anomaly-item-left {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .anomaly-item-right {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .severity-badge {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
          text-transform: capitalize;
        }

        .severity-normal {
          background-color: #e3f2fd;
          color: #1565c0;
        }

        .severity-high {
          background-color: #fff3e0;
          color: #ef6c00;
        }

        .severity-urgent {
          background-color: #ffebee;
          color: #c62828;
        }

        .anomaly-type {
          font-weight: 500;
          color: #333;
        }

        .anomaly-date {
          font-size: 12px;
          color: #666;
        }

        .resolved-badge {
          font-size: 12px;
          color: #388e3c;
          padding: 4px 8px;
          background-color: #e8f5e9;
          border-radius: 4px;
        }

        .resolve-button {
          padding: 6px 12px;
          background-color: #1976d2;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 12px;
        }

        .resolve-button:hover {
          background-color: #1565c0;
        }

        .auto-pause-indicator {
          display: inline-block;
          padding: 4px 8px;
          background-color: #ffebee;
          color: #c62828;
          font-size: 11px;
          border-radius: 4px;
          margin-bottom: 8px;
        }

        .anomaly-summary {
          display: flex;
          gap: 16px;
          font-size: 14px;
          color: #555;
        }

        .anomaly-details-expanded {
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid #e0e0e0;
        }

        .anomaly-type-details p {
          margin: 4px 0;
          font-size: 13px;
          color: #666;
        }

        .related-purchases {
          margin-top: 8px;
          padding: 8px;
          background-color: #e3f2fd;
          border-radius: 4px;
          font-size: 13px;
          color: #1565c0;
          cursor: pointer;
        }

        .related-purchases:hover {
          background-color: #bbdefb;
        }

        .resolution-notes {
          margin-top: 8px;
          padding: 8px;
          background-color: #f5f5f5;
          border-radius: 4px;
          font-size: 13px;
        }

        .loading-state,
        .empty-state,
        .error-state {
          padding: 32px;
          text-align: center;
          color: #666;
        }

        .error-state button {
          margin-top: 12px;
          padding: 8px 16px;
          background-color: #1976d2;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
        }

        .load-more-button {
          display: block;
          width: 100%;
          margin-top: 16px;
          padding: 12px;
          background-color: #f5f5f5;
          border: 1px solid #ddd;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
        }

        .load-more-button:hover {
          background-color: #e0e0e0;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal-content {
          background: white;
          padding: 24px;
          border-radius: 8px;
          min-width: 400px;
          max-width: 500px;
        }

        .modal-content h2 {
          margin: 0 0 20px 0;
          font-size: 20px;
        }

        .form-group {
          margin-bottom: 16px;
        }

        .form-group label {
          display: block;
          margin-bottom: 6px;
          font-size: 14px;
          font-weight: 500;
        }

        .form-group select,
        .form-group textarea {
          width: 100%;
          padding: 10px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
        }

        .modal-actions {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          margin-top: 24px;
        }

        .cancel-button {
          padding: 10px 20px;
          background-color: #f5f5f5;
          border: 1px solid #ddd;
          border-radius: 4px;
          cursor: pointer;
        }

        .submit-button {
          padding: 10px 20px;
          background-color: #1976d2;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
        }

        .submit-button:hover {
          background-color: #1565c0;
        }

        .submit-button:disabled,
        .cancel-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export default AnomalyHistoryPanel;
